import { useState, useEffect, useMemo, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import qs from 'query-string';
import shuffle from 'lodash/shuffle';
import { Link, useNavigate } from 'react-router-dom';

import * as API from "../api";
import { COUNTRY_CODES } from "../codes";
import { INDEX_SLATES, INDEX_ANIMATIONS, INDEX_JOB_TITLES } from '../options';
import { Geocoder, isLocationRemote } from '../tools';
import { MapMapbox } from "../components/Map";
import { Badge } from "@/components/ui/badge";


const country = 'PL'; // TODO: READ FROM nginx
const countryData = COUNTRY_CODES[country];
const INITIAL_CENTER = countryData || { lat: 45.164, lng: -101.030 };
const INITIAL_ZOOM = countryData ? (countryData.zoom || 3) : 4;

function getSlateCount() {
    if (window.innerWidth > 1920) return 9;
    if (window.innerWidth > 1280) return 7;
    if (window.innerWidth > 1024) return 6;
    return 5;
}


function SlateBar() {
    const [slates] = useState(() => shuffle(INDEX_SLATES).slice(0, getSlateCount()));
    const [slatesCounts, setSlatesCounts] = useState({});

    useEffect(() => {
        slates.forEach((slate, i) => {
            API.itemCount({
                ...slate.urlParams,
                ...slate.searchParams,
                ...slate.countParams,
            }).then(count => {
                setSlatesCounts(prev => ({ ...prev, [i]: count }));
            });
        });
    }, [slates]);

    return (
        <div className="flex justify-center z-[100] shadow-sm max-sm:hidden">
            {slates.map((slate, i) => {
                const { center: { lat, lng }, zoom } = slate.urlParams;
                const to = {
                    pathname: `/search/${lat}/${lng}/${zoom}`,
                    search: qs.stringify(slate.searchParams)
                };
                return (
                    <Link key={slate.line1} to={to}
                        className="slate group relative flex flex-col justify-center items-center text-white no-underline overflow-hidden px-5 w-[calc(100vw/5)] h-[calc(100vh/5)]">
                        <div
                            className="slate-bg absolute inset-0 bg-cover bg-center bg-blend-overlay -z-10 transition-[transform,background-color] duration-200 ease-in-out group-hover:scale-110"
                            style={{ backgroundImage: `url(${slate.image})`, backgroundColor: slate.color }}
                        />
                        <h2 className="m-0 leading-none font-medium mb-1 text-xl lg:text-[27px]">{slate.line1}</h2>
                        <h3 className="m-0 leading-none font-light text-white text-center mb-2.5 text-[17px] lg:text-[23px]">{slate.line2}</h3>
                        <Badge className="bg-black/80 hover:bg-black/80 text-white rounded px-2 text-[13px] lg:text-[17px] font-normal">
                            {slatesCounts[i]} JOBS
                        </Badge>
                    </Link>
                );
            })}
        </div>
    );
}


export default function IndexView() {
    const navigate = useNavigate();

    const backgrounds = useMemo(() => shuffle(INDEX_ANIMATIONS), []);
    const titles = useMemo(() => shuffle(INDEX_JOB_TITLES), []);

    const [totalItems, setTotalItems] = useState(null);
    const [titleIndex, setTitleIndex] = useState(0);
    const [backgroundIndex, setBackgroundIndex] = useState(0);
    const [clusters, setClusters] = useState([]);
    const [formSearch, setFormSearch] = useState('');
    const [formLocation, setFormLocation] = useState('');

    const formSearchRef = useRef(formSearch);
    const formLocationRef = useRef(formLocation);
    formSearchRef.current = formSearch;
    formLocationRef.current = formLocation;

    useEffect(() => {
        API.itemCount().then(setTotalItems);
        API.clusterQuery({ zoom: INITIAL_ZOOM }).then(setClusters);

        const timer = setInterval(() => {
            setBackgroundIndex(prev => (prev + 1) % backgrounds.length);
            setTitleIndex(prev => (prev + 1) % titles.length);
        }, 7000);

        return () => clearInterval(timer);
    }, [backgrounds.length, titles.length]);

    const onMapClick = (instance, e) => {
        const { lat, lng } = e.lngLat;
        navigate(`/search/${lat.toFixed(4)}/${lng.toFixed(4)}/5`);
    };

    const onSubmit = (e) => {
        e.preventDefault();

        const search = formSearchRef.current;
        const location = formLocationRef.current;
        let query = {};

        if (isLocationRemote(location)) query.remote = true;
        if (search) query.search = search;

        if (location && !isLocationRemote(location)) {
            query.location = location;
            Geocoder.get(location).then(results => {
                const { center, zoom } = results;
                const { lat, lng } = center;
                navigate({
                    pathname: `/search/${lat.toFixed(4)}/${lng.toFixed(4)}/${zoom}/`,
                    search: qs.stringify(query),
                });
            });
        } else {
            navigate({
                pathname: `/search/-1.2999/-32.6511/2/`,
                search: qs.stringify(query),
            });
        }
    };

    return (
        <div className="flex-1 flex flex-col font-light leading-tight antialiased text-white">

            {/* Hero: form panel (left) + map (right) */}
            <div className="flex flex-1 z-10">

                {/* Form panel */}
                <div className="flex-[1_1_60%] relative flex flex-col justify-center px-11 max-sm:px-6">

                    {/* Carousel background images */}
                    <div className="absolute inset-0 -z-10">
                        <CSSTransition classNames="carousel" timeout={{ exit: 2200, enter: 2200 }}>
                            <div key={backgroundIndex}
                                className="absolute inset-0 bg-cover bg-center"
                                style={{ backgroundImage: `url(${backgrounds[backgroundIndex].background})` }}
                            />
                        </CSSTransition>
                    </div>

                    {/* Dark gradient overlay on top of background */}
                    <div className="absolute inset-0 bg-gradient-to-b from-brand to-[rgba(27,24,43,0.72)]" />

                    {/* Page content */}
                    <div className="relative z-10 flex flex-col">

                        {/* Animated motto */}
                        <h1 className="text-[30px] sm:text-[40px] md:text-[60px] font-medium m-0 leading-tight">
                            We know who is hiring
                        </h1>
                        <h2 className="relative h-[1em] text-[30px] sm:text-[35px] md:text-[50px] font-light m-0 leading-[1.02]">
                            <CSSTransition classNames="carousel" timeout={{ exit: 2200, enter: 2200 }}>
                                <span key={titleIndex} className="absolute w-full">{titles[titleIndex]}</span>
                            </CSSTransition>
                        </h2>
                        <h3 className="relative h-[1em] text-[30px] sm:text-[35px] md:text-[50px] font-light m-0 leading-[1.02]">
                            <CSSTransition classNames="carousel" timeout={{ exit: 2200, enter: 2200 }}>
                                <span key={backgroundIndex} className="absolute w-full">in {backgrounds[backgroundIndex].text}</span>
                            </CSSTransition>
                        </h3>

                        {/* Search form */}
                        <form onSubmit={onSubmit} className="mt-8 flex flex-wrap rounded overflow-hidden">
                            <fieldset className="relative flex-[3_0_200px] m-0 p-0 border-0">
                                <label className="absolute top-2 left-3 text-[11px] font-medium text-gray-400 tracking-widest z-10">
                                    JOB SEARCH
                                </label>
                                <input
                                    name="search"
                                    type="text"
                                    autoComplete="off"
                                    value={formSearch}
                                    onChange={e => setFormSearch(e.target.value)}
                                    className="w-full h-16 pt-6 pb-2 px-3 text-lg text-gray-800 bg-white border-0 border-r border-gray-200 rounded-none focus:outline-none"
                                />
                            </fieldset>
                            <fieldset className="relative flex-[3_0_200px] m-0 p-0 border-0">
                                <label className="absolute top-2 left-3 text-[11px] font-medium text-gray-400 tracking-widest z-10">
                                    LOCATION
                                </label>
                                <input
                                    name="location"
                                    type="text"
                                    autoComplete="off"
                                    value={formLocation}
                                    onChange={e => setFormLocation(e.target.value)}
                                    className="w-full h-16 pt-6 pb-2 px-3 text-lg text-gray-800 bg-white border-0 rounded-none focus:outline-none"
                                />
                            </fieldset>
                            <button
                                type="submit"
                                className="flex-[1_0_auto] h-16 bg-cyan hover:bg-cyan/90 text-white text-base font-semibold tracking-widest cursor-pointer border-0 rounded-none"
                            >
                                SEARCH
                            </button>
                        </form>

                        {/* Job count */}
                        <div className="text-[17px] sm:text-[23px] text-white self-end leading-[2.22]">
                            search <span className="text-cyan">{totalItems}</span> jobs worldwide
                        </div>

                    </div>
                </div>

                {/* Map — hidden below md breakpoint (1280px) */}
                <div className="flex-[1_1_40%] max-md:hidden">
                    <MapMapbox
                        center={INITIAL_CENTER}
                        zoom={INITIAL_ZOOM}
                        onClick={onMapClick}
                        clusters={clusters}
                        disableZoom
                        disableDoubleClickZoom
                        className="h-full w-full"
                    >
                        <div className="absolute inset-0 z-[100] shadow-[inset_0_0_8px_0_rgba(0,0,0,0.75)] pointer-events-none" />
                    </MapMapbox>
                </div>

            </div>

            <SlateBar />

        </div>
    );
}
